/**
 * VacationContext.js
 * Manages vacation bookings and vacation accounts for staff members
 * Added vacation account management and auto-recalculation
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import VacationService from '../services/VacationService';
import VacationAccountService from '../services/VacationAccountService';
import { VacationDateRange, VacationHelpers } from '../models/VacationModels';
import { useAuth } from './AuthContext';
import { useStaff } from './StaffContext';
import { useDepartment } from './DepartmentContext';

const VacationContext = createContext();

export const useVacation = () => {
  const context = useContext(VacationContext);
  if (!context) {
    throw new Error('useVacation must be used within VacationProvider');
  }
  return context;
};

export const VacationProvider = ({ children }) => {
  const { currentUser, currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  const { staffMembers, loading: staffLoading } = useStaff();
  const { departments, loading: departmentsLoading } = useDepartment();

  // ========== EXISTING STATE ==========
  const [vacations, setVacations] = useState([]);
  const [rawVacations, setRawVacations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ========== VACATION ACCOUNT STATE ==========
  const [vacationAccount, setVacationAccount] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState(null);

  const activeListenerRef = useRef(null);

  const staffMembersRef = useRef(staffMembers);
  const departmentsRef = useRef(departments);
  const staffLoadingRef = useRef(staffLoading);
  const departmentsLoadingRef = useRef(departmentsLoading);

  useEffect(() => {
    staffMembersRef.current = staffMembers;
    departmentsRef.current = departments;
    staffLoadingRef.current = staffLoading;
    departmentsLoadingRef.current = departmentsLoading;
  }, [staffMembers, departments, staffLoading, departmentsLoading]);

  const enrichVacationStable = useCallback((vacation) => {
    if (staffLoadingRef.current || departmentsLoadingRef.current) {
      vacation.staffName = 'Lädt...';
      vacation.departmentName = 'Lädt...';
      return vacation;
    }

    const staff = staffMembersRef.current.find(s => s.id === vacation.staffId);
    const department = departmentsRef.current.find(d => d.id === vacation.departmentId);
    
    vacation.staffName = staff?.name || 'Unbekannter Mitarbeiter';
    vacation.departmentName = department?.name || 'Unbekannte Abteilung';
    return vacation;
  }, []);

  const enrichVacationsStable = useCallback((vacationsList) => {
    return vacationsList.map(enrichVacationStable);
  }, [enrichVacationStable]);

  /**
   * Cleanup listener on unmount
   */
  useEffect(() => {
    return () => {
      if (activeListenerRef.current) {
        activeListenerRef.current();
        activeListenerRef.current = null;
      }
    };
  }, []);

  /**
   * Clear error after timeout
   */
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  /**
   * Clear account error after timeout
   */
  useEffect(() => {
    if (accountError) {
      const timer = setTimeout(() => setAccountError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [accountError]);

  /**
   * Re-enrich vacations when staff or department data changes
   */
  useEffect(() => {
    if (rawVacations.length > 0 && !staffLoading && !departmentsLoading) {
      console.log('Re-enriching vacations with updated staff/department data');
      const enriched = rawVacations.map(enrichVacationStable);
      setVacations(enriched);
    }
  }, [staffMembers, departments, staffLoading, departmentsLoading, rawVacations, enrichVacationStable]);

  // ==================== VACATION ACCOUNT METHODS ====================

  /**
   * Load vacation account for a specific staff member and year
   * Automatically recalculates used days to ensure data is up-to-date
   */
  const loadVacationAccount = useCallback(async (staffId, year = selectedYear) => {
    if (!companyId) throw new Error('No company ID');

    setAccountLoading(true);
    setAccountError(null);

    try {
      console.log(`Loading vacation account for staff ${staffId}, year ${year}`);
      
      let account = await VacationAccountService.getAccount(companyId, staffId, year);
      
      if (account) {
        console.log('Recalculating vacation account to ensure accurate used days...');
        account = await VacationAccountService.recalculateAccount(companyId, staffId, year);
        console.log(`Account recalculated - Used Days: ${account.usedDays}, Remaining: ${account.remainingDays}`);
      } else {
        console.log(`No vacation account found for ${staffId} in year ${year}`);
      }
      
      setVacationAccount(account);
      return account;
    } catch (err) {
      console.error('Error loading vacation account:', err);
      setAccountError(err.message);
      throw err;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId, selectedYear]);

  /**
   * Create a new vacation account for a staff member
   */
  const createVacationAccount = useCallback(async (staffId, year, data) => {
    if (!companyId) throw new Error('No company ID');

    setAccountLoading(true);
    setAccountError(null);

    try {
      const account = await VacationAccountService.createAccount(companyId, staffId, year, data);
      setVacationAccount(account);
      return account;
    } catch (err) {
      console.error('Error creating vacation account:', err);
      setAccountError(err.message);
      throw err;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId]);

  /**
   * Initialize account with automatic carryover from previous year
   */
  const initializeAccountWithCarryover = useCallback(async (staffId, year, baseEntitlement, carryOverLimit = 0) => {
    if (!companyId) throw new Error('No company ID');

    setAccountLoading(true);
    setAccountError(null);

    try {
      const account = await VacationAccountService.initializeAccountWithCarryover(
        companyId,
        staffId,
        year,
        baseEntitlement,
        carryOverLimit
      );
      setVacationAccount(account);
      return account;
    } catch (err) {
      console.error('Error initializing account with carryover:', err);
      setAccountError(err.message);
      throw err;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId]);

  /**
   * Add an adjustment to vacation account
   */
  const addVacationAdjustment = useCallback(async (staffId, year, adjustment) => {
    if (!companyId) throw new Error('No company ID');

    setAccountLoading(true);
    setAccountError(null);

    try {
      const updatedAccount = await VacationAccountService.addAdjustment(
        companyId,
        staffId,
        year,
        {
          ...adjustment,
          createdBy: currentUser.uid,
          createdAt: new Date()
        }
      );
      setVacationAccount(updatedAccount);
      return updatedAccount;
    } catch (err) {
      console.error('Error adding adjustment:', err);
      setAccountError(err.message);
      throw err;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId, currentUser]);

  /**
   * Delete a specific adjustment from a vacation account by its index
   * Calls the service to remove the item from the Firestore array,
   * then updates the local vacationAccount state with the recalculated account
   * @param {string} staffId
   * @param {string} year
   * @param {number} adjustmentIndex - The index of the adjustment to remove
   * @returns {Promise<VacationAccount>} The updated account
   */
  const deleteVacationAdjustment = useCallback(async (staffId, year, adjustmentIndex) => {
    if (!companyId) throw new Error('No company ID');

    setAccountLoading(true);
    setAccountError(null);

    try {
      const updatedAccount = await VacationAccountService.deleteAdjustment(
        companyId,
        staffId,
        year,
        adjustmentIndex
      );
      setVacationAccount(updatedAccount);
      return updatedAccount;
    } catch (err) {
      console.error('Error deleting adjustment:', err);
      setAccountError(err.message);
      throw err;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId]);

  /**
   * Delete vacation account for a specific year
   */
  const deleteVacationAccount = useCallback(async (staffId, year) => {
    try {
      setAccountLoading(true);
      setAccountError(null);

      if (!companyId) {
        throw new Error('Company ID is required');
      }

      await VacationAccountService.deleteVacationAccount(companyId, staffId, year);

      if (selectedYear === year) {
        setVacationAccount(null);
      }

      console.log('Vacation account deleted successfully');
    } catch (error) {
      console.error('Error in deleteVacationAccount:', error);
      setAccountError(error.message);
      throw error;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId, selectedYear]);

  /**
   * Update vacation account settings
   */
  const updateVacationAccountSettings = useCallback(async (staffId, year, fields) => {
    if (!companyId) throw new Error('No company ID');

    setAccountLoading(true);
    setAccountError(null);

    try {
      const updatedAccount = await VacationAccountService.updateAccountSettings(
        companyId,
        staffId,
        year,
        fields
      );
      setVacationAccount(updatedAccount);
      return updatedAccount;
    } catch (err) {
      console.error('Error updating account settings:', err);
      setAccountError(err.message);
      throw err;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId]);

  /**
   * Recalculate vacation account based on actual usage
   */
  const recalculateVacationAccount = useCallback(async (staffId, year) => {
    if (!companyId) throw new Error('No company ID');

    try {
      const updatedAccount = await VacationAccountService.recalculateAccount(
        companyId,
        staffId,
        year
      );
      setVacationAccount(updatedAccount);
      return updatedAccount;
    } catch (err) {
      console.error('Error recalculating account:', err);
      throw err;
    }
  }, [companyId]);

  /**
   * Get all vacation accounts for a staff member
   */
  const getAllVacationAccounts = useCallback(async (staffId) => {
    if (!companyId) throw new Error('No company ID');

    try {
      return await VacationAccountService.getAllAccounts(companyId, staffId);
    } catch (err) {
      console.error('Error getting all accounts:', err);
      throw err;
    }
  }, [companyId]);

  /**
   * Check if account exists for a specific year
   */
  const checkAccountExists = useCallback(async (staffId, year) => {
    if (!companyId) throw new Error('No company ID');

    try {
      return await VacationAccountService.accountExists(companyId, staffId, year);
    } catch (err) {
      console.error('Error checking account existence:', err);
      throw err;
    }
  }, [companyId]);

  /**
   * Expire carryover days manually
   */
  const expireCarryover = useCallback(async (staffId, year) => {
    if (!companyId) throw new Error('No company ID');

    setAccountLoading(true);
    setAccountError(null);

    try {
      const updatedAccount = await VacationAccountService.expireCarryover(
        companyId,
        staffId,
        year
      );
      setVacationAccount(updatedAccount);
      return updatedAccount;
    } catch (err) {
      console.error('Error expiring carryover:', err);
      setAccountError(err.message);
      throw err;
    } finally {
      setAccountLoading(false);
    }
  }, [companyId]);

  /**
   * Check if staff has sufficient vacation days
   */
  const checkSufficientDays = useCallback(async (staffId, year, requestedDays) => {
    if (!companyId) throw new Error('No company ID');

    try {
      const account = await VacationAccountService.getAccount(companyId, staffId, year);
      
      if (!account) {
        return {
          hasSufficient: false,
          message: 'No vacation account found for this year'
        };
      }

      const hasSufficient = account.hasSufficientDays(requestedDays);
      
      return {
        hasSufficient,
        remainingDays: account.remainingDays,
        requestedDays,
        message: hasSufficient 
          ? 'Sufficient vacation days available'
          : `Insufficient days. Available: ${account.remainingDays}, Requested: ${requestedDays}`
      };
    } catch (err) {
      console.error('Error checking sufficient days:', err);
      throw err;
    }
  }, [companyId]);

  // ==================== EXISTING VACATION METHODS ====================

  /**
   * Add a single vacation day
   */
  const addVacation = useCallback(async (vacationData) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const vacationDate = new Date(vacationData.date);
      const year = vacationDate.getFullYear().toString();
      const account = await VacationAccountService.getAccount(companyId, vacationData.staffId, year);
      
      if (account && !account.hasSufficientDays(1)) {
        throw new Error(`Insufficient vacation days. Available: ${account.remainingDays} days`);
      }

      const result = await VacationService.addVacation(companyId, {
        ...vacationData,
        createdBy: currentUser.uid
      });

      if (account) {
        await VacationAccountService.recalculateAccount(companyId, vacationData.staffId, year);
      }

      if (!activeListenerRef.current) {
        setRawVacations(prev => [...prev, result]);
        const enrichedVacation = enrichVacationStable(result);
        setVacations(prev => [...prev, enrichedVacation]);
      }

      return { success: true, vacation: result };
    } catch (err) {
      console.error('Error adding vacation:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, currentUser, enrichVacationStable]);

  /**
   * Add vacation date range (multiple days)
   */
  const addVacationRange = useCallback(async (staffId, departmentId, startDate, endDate) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const dateRange = new VacationDateRange(startDate, endDate);
      const totalDays = dateRange.getDates().length;
      
      const year = new Date(startDate).getFullYear().toString();
      const account = await VacationAccountService.getAccount(companyId, staffId, year);
      
      if (account && !account.hasSufficientDays(totalDays)) {
        throw new Error(`Insufficient vacation days. Available: ${account.remainingDays}, Requested: ${totalDays}`);
      }

      const result = await VacationService.addVacationRange(
        companyId,
        staffId,
        departmentId,
        startDate,
        endDate,
        currentUser.uid
      );

      if (account) {
        await VacationAccountService.recalculateAccount(companyId, staffId, year);
      }

      if (!activeListenerRef.current) {
        setRawVacations(prev => [...prev, ...result.vacations]);
        const enrichedVacations = enrichVacationsStable(result.vacations);
        setVacations(prev => [...prev, ...enrichedVacations]);
      }

      return { 
        success: true, 
        count: result.count,
        totalMinutes: result.totalMinutes,
        totalHours: result.totalHours,
        vacations: result.vacations
      };
    } catch (err) {
      console.error('Error adding vacation range:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, currentUser, enrichVacationsStable]);

  /**
   * Preview vacation date range
   */
  const previewVacationRange = useCallback(async (staffId, departmentId, startDate, endDate) => {
    if (!companyId) throw new Error('No company ID');

    try {
      const dateRange = new VacationDateRange(startDate, endDate);
      const dates = dateRange.getDates();

      const preview = await Promise.all(
        dates.map(async (date) => {
          const durationMinutes = await VacationService.calculateTotalMinutesFromShifts(
            companyId,
            staffId,
            date,
            departmentId
          );
          return {
            date,
            dateString: VacationHelpers.formatDate(date),
            durationMinutes,
            formattedDuration: VacationHelpers.formatDurationFromMinutes(durationMinutes),
            hours: durationMinutes / 60,
            hasShift: durationMinutes > 0
          };
        })
      );

      const totalMinutes = preview.reduce((sum, item) => sum + item.durationMinutes, 0);
      const totalHours = totalMinutes / 60;
      const totalDays = preview.length;

      const year = new Date(startDate).getFullYear().toString();
      const account = await VacationAccountService.getAccount(companyId, staffId, year);
      
      let balanceInfo = null;
      if (account) {
        balanceInfo = {
          remainingDays: account.remainingDays,
          requestedDays: totalDays,
          hasSufficient: account.hasSufficientDays(totalDays),
          totalEntitlement: account.totalEntitlement,
          usedDays: account.usedDays
        };
      }

      return {
        preview,
        totalDays,
        totalMinutes,
        totalHours,
        formattedTotalDuration: VacationHelpers.formatDurationFromMinutes(totalMinutes),
        startDate,
        endDate,
        balanceInfo
      };
    } catch (err) {
      console.error('Error previewing vacation range:', err);
      throw err;
    }
  }, [companyId]);

  /**
   * Update vacation
   */
  const updateVacation = useCallback(async (vacationId, updates) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const vacation = vacations.find(v => v.id === vacationId);
      if (!vacation) throw new Error('Vacation not found');

      await VacationService.updateVacation(
        companyId,
        vacationId,
        updates,
        currentUser.uid
      );

      const year = new Date(vacation.date).getFullYear().toString();
      await VacationAccountService.recalculateAccount(companyId, vacation.staffId, year);

      if (!activeListenerRef.current) {
        const updateFn = (v) => {
          if (v.id === vacationId) {
            Object.assign(v, updates, { updatedAt: new Date() });
          }
          return v;
        };

        setRawVacations(prev => prev.map(updateFn));
        setVacations(prev => prev.map(v => {
          if (v.id === vacationId) {
            Object.assign(v, updates, { updatedAt: new Date() });
            return enrichVacationStable(v);
          }
          return v;
        }));
      }

      return { success: true };
    } catch (err) {
      console.error('Error updating vacation:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, currentUser, vacations, enrichVacationStable]);

  /**
   * Delete vacation
   */
  const deleteVacation = useCallback(async (vacationId) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const vacation = vacations.find(v => v.id === vacationId);
      if (!vacation) throw new Error('Vacation not found');

      await VacationService.deleteVacation(companyId, vacationId);

      const year = new Date(vacation.date).getFullYear().toString();
      await VacationAccountService.recalculateAccount(companyId, vacation.staffId, year);

      if (!activeListenerRef.current) {
        setRawVacations(prev => prev.filter(v => v.id !== vacationId));
        setVacations(prev => prev.filter(v => v.id !== vacationId));
      }

      return { success: true };
    } catch (err) {
      console.error('Error deleting vacation:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, vacations]);

  // ==================== EXISTING METHODS (UNCHANGED) ====================

  const getVacationById = useCallback((vacationId) => {
    return vacations.find(v => v.id === vacationId);
  }, [vacations]);

  const fetchVacationsByDepartment = useCallback(async (departmentId, startDate, endDate) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const result = await VacationService.getVacationsByDepartment(
        companyId,
        departmentId,
        startDate,
        endDate
      );

      setRawVacations(result);
      const enrichedResult = enrichVacationsStable(result);
      setVacations(enrichedResult);
      return enrichedResult;
    } catch (err) {
      console.error('Error fetching vacations by department:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, enrichVacationsStable]);

  const fetchVacationsByStaff = useCallback(async (staffId, startDate, endDate) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const result = await VacationService.getVacationsByStaff(
        companyId,
        staffId,
        startDate,
        endDate
      );

      setRawVacations(result);
      const enrichedResult = enrichVacationsStable(result);
      setVacations(enrichedResult);
      return enrichedResult;
    } catch (err) {
      console.error('Error fetching vacations by staff:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, enrichVacationsStable]);

  const fetchVacationsByStaffAndMonth = useCallback(async (staffId, monthOrDate, year) => {
    if (!companyId) throw new Error('No company ID');
    
    if (!staffId) {
      console.error('Staff ID is missing');
      throw new Error('Staff ID is required');
    }

    let startDate, endDate;

    if (monthOrDate instanceof Date) {
      const date = monthOrDate;
      const month = date.getMonth();
      const yearFromDate = date.getFullYear();
      
      startDate = new Date(yearFromDate, month, 1);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(yearFromDate, month + 1, 0);
      endDate.setHours(23, 59, 59, 999);
    } else {
      const month = monthOrDate;
      
      if (typeof month !== 'number' || month < 1 || month > 12) {
        console.error('Invalid month:', month);
        throw new Error(`Invalid month: ${month} (must be 1-12)`);
      }
      
      if (!year || typeof year !== 'number' || year < 2000 || year > 2100) {
        console.error('Invalid year:', year);
        throw new Error(`Invalid year: ${year} (must be 2000-2100)`);
      }

      startDate = new Date(year, month - 1, 1);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(year, month, 0);
      endDate.setHours(23, 59, 59, 999);
    }

    return await fetchVacationsByStaff(staffId, startDate, endDate);
  }, [companyId, fetchVacationsByStaff]);

  const fetchVacationsByDepartmentAndMonth = useCallback(async (departmentId, monthOrDate, year) => {
    if (!companyId) throw new Error('No company ID');
    
    if (!departmentId) {
      console.error('Department ID is missing');
      throw new Error('Department ID is required');
    }

    let startDate, endDate;

    if (monthOrDate instanceof Date) {
      const date = monthOrDate;
      const month = date.getMonth();
      const yearFromDate = date.getFullYear();
      
      startDate = new Date(yearFromDate, month, 1);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(yearFromDate, month + 1, 0);
      endDate.setHours(23, 59, 59, 999);
    } else {
      const month = monthOrDate;
      
      if (typeof month !== 'number' || month < 1 || month > 12) {
        console.error('Invalid month:', month);
        throw new Error(`Invalid month: ${month} (must be 1-12)`);
      }
      
      if (!year || typeof year !== 'number' || year < 2000 || year > 2100) {
        console.error('Invalid year:', year);
        throw new Error(`Invalid year: ${year} (must be 2000-2100)`);
      }

      startDate = new Date(year, month - 1, 1);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(year, month, 0);
      endDate.setHours(23, 59, 59, 999);
    }

    return await fetchVacationsByDepartment(departmentId, startDate, endDate);
  }, [companyId, fetchVacationsByDepartment]);

  const fetchVacationsByDateRange = useCallback(async (startDate, endDate, departmentIds = null) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      let result;
      if (departmentIds && departmentIds.length > 0) {
        const allVacations = [];
        for (const deptId of departmentIds) {
          const deptVacations = await VacationService.getVacationsByDepartment(
            companyId,
            deptId,
            startDate,
            endDate
          );
          allVacations.push(...deptVacations);
        }
        result = allVacations;
      } else {
        result = await VacationService.getVacationsByDateRange(
          companyId,
          startDate,
          endDate
        );
      }

      setRawVacations(result);
      const enrichedResult = enrichVacationsStable(result);
      setVacations(enrichedResult);
      return enrichedResult;
    } catch (err) {
      console.error('Error fetching vacations by date range:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, enrichVacationsStable]);

  const fetchVacationsForDay = useCallback(async (date, departmentIds = null) => {
    if (!companyId) throw new Error('No company ID');

    try {
      const result = await VacationService.getVacationsForDay(
        companyId,
        date,
        departmentIds
      );

      return enrichVacationsStable(result);
    } catch (err) {
      console.error('Error fetching vacations for day:', err);
      throw err;
    }
  }, [companyId, enrichVacationsStable]);

  const getCurrentWeekVacations = useCallback(async () => {
    if (!companyId) throw new Error('No company ID');

    try {
      const result = await VacationService.getCurrentWeekVacations(companyId);
      return enrichVacationsStable(result);
    } catch (err) {
      console.error('Error fetching current week vacations:', err);
      throw err;
    }
  }, [companyId, enrichVacationsStable]);

  const getMonthSummary = useCallback(async (month, year, departmentIds = null) => {
    if (!companyId) throw new Error('No company ID');

    try {
      const staffMap = {};
      staffMembersRef.current.forEach(staff => {
        staffMap[staff.id] = staff;
      });

      const summary = await VacationService.getMonthSummary(
        companyId,
        month,
        year,
        departmentIds,
        staffMap
      );

      const enrichedTopStaff = summary.getTopStaff().map(staffStat => {
        const staff = staffMembersRef.current.find(s => s.id === staffStat.staffId);
        return {
          ...staffStat,
          name: staff?.name || staffStat.name || 'Unbekannter Mitarbeiter',
          formattedDuration: VacationHelpers.formatDurationFromMinutes(staffStat.minutes)
        };
      });

      return {
        totalDays: summary.totalDays,
        totalMinutes: summary.totalMinutes,
        totalHours: summary.getTotalHours(),
        formattedTotalDuration: summary.getFormattedTotalDuration(),
        formattedTotalHours: summary.getFormattedTotalHours(),
        topStaff: enrichedTopStaff,
        byStaff: Array.from(summary.byStaff.values()).map(staffStat => {
          const staff = staffMembersRef.current.find(s => s.id === staffStat.staffId);
          return {
            ...staffStat,
            name: staff?.name || staffStat.name || 'Unbekannter Mitarbeiter',
            formattedDuration: VacationHelpers.formatDurationFromMinutes(staffStat.minutes)
          };
        })
      };
    } catch (err) {
      console.error('Error getting month summary:', err);
      throw err;
    }
  }, [companyId]);

  const subscribeToVacations = useCallback((startDate, endDate, departmentIds = null) => {
    if (!companyId) return;

    console.log('Setting up vacation subscription');

    if (activeListenerRef.current) {
      console.log('Cleaning up previous listener');
      activeListenerRef.current();
      activeListenerRef.current = null;
    }

    setLoading(true);

    const unsubscribe = VacationService.subscribeToVacations(
      companyId,
      startDate,
      endDate,
      (updatedVacations) => {
        console.log('Received vacation update:', updatedVacations.length, 'items');
        setRawVacations(updatedVacations);
        const enriched = updatedVacations.map(v => {
          const staff = staffMembersRef.current.find(s => s.id === v.staffId);
          const department = departmentsRef.current.find(d => d.id === v.departmentId);
          v.staffName = staff?.name || 'Unbekannter Mitarbeiter';
          v.departmentName = department?.name || 'Unbekannte Abteilung';
          return v;
        });
        setVacations(enriched);
        setLoading(false);
      },
      departmentIds
    );

    activeListenerRef.current = unsubscribe;

    return unsubscribe;
  }, [companyId]);

  const subscribeToVacationsForDay = useCallback((date, departmentIds = null) => {
    if (!companyId) return;

    if (activeListenerRef.current) {
      activeListenerRef.current();
      activeListenerRef.current = null;
    }

    const unsubscribe = VacationService.subscribeToVacationsForDay(
      companyId,
      date,
      (updatedVacations) => {
        setRawVacations(updatedVacations);
        const enriched = updatedVacations.map(v => {
          const staff = staffMembersRef.current.find(s => s.id === v.staffId);
          const department = departmentsRef.current.find(d => d.id === v.departmentId);
          v.staffName = staff?.name || 'Unbekannter Mitarbeiter';
          v.departmentName = department?.name || 'Unbekannte Abteilung';
          return v;
        });
        setVacations(enriched);
      },
      departmentIds
    );

    activeListenerRef.current = unsubscribe;

    return unsubscribe;
  }, [companyId]);

  const unsubscribe = useCallback(() => {
    if (activeListenerRef.current) {
      console.log('Unsubscribing from vacation listener');
      activeListenerRef.current();
      activeListenerRef.current = null;
    }
  }, []);

  const groupVacationsByDate = useCallback(() => {
    return VacationService.groupVacationsByDate(vacations);
  }, [vacations]);

  const groupVacationsByStaff = useCallback(() => {
    return VacationService.groupVacationsByStaff(vacations);
  }, [vacations]);

  const getVacationsForDate = useCallback((date) => {
    const dateStr = date instanceof Date 
      ? date.toISOString().split('T')[0]
      : date;
    
    return vacations.filter(v => v.getDateString() === dateStr);
  }, [vacations]);

  const getVacationsForStaff = useCallback((staffId) => {
    return vacations.filter(v => v.staffId === staffId);
  }, [vacations]);

  const hasVacationOnDate = useCallback((staffId, date) => {
    const dateStr = date instanceof Date 
      ? date.toISOString().split('T')[0]
      : date;
    
    return vacations.some(
      v => v.staffId === staffId && v.getDateString() === dateStr
    );
  }, [vacations]);

  const getVacationCountForDate = useCallback((date) => {
    return getVacationsForDate(date).length;
  }, [getVacationsForDate]);

  const getTotalMinutesForDate = useCallback((date) => {
    const dayVacations = getVacationsForDate(date);
    return dayVacations.reduce((sum, v) => sum + (v.durationMinutes || 0), 0);
  }, [getVacationsForDate]);

  const getTotalHoursForDate = useCallback((date) => {
    const totalMinutes = getTotalMinutesForDate(date);
    return totalMinutes / 60;
  }, [getTotalMinutesForDate]);

  const getFormattedDurationForDate = useCallback((date) => {
    const totalMinutes = getTotalMinutesForDate(date);
    return VacationHelpers.formatDurationFromMinutes(totalMinutes);
  }, [getTotalMinutesForDate]);

  const clearVacations = useCallback(() => {
    setVacations([]);
    setRawVacations([]);
  }, []);

  const value = {
    // State
    vacations,
    loading,
    error,

    // CRUD operations
    addVacation,
    addVacationRange,
    previewVacationRange,
    updateVacation,
    deleteVacation,
    getVacationById,

    // Fetch operations
    fetchVacationsByDepartment,
    fetchVacationsByStaff,
    fetchVacationsByDateRange,
    fetchVacationsForDay,
    fetchVacationsByStaffAndMonth,
    fetchVacationsByDepartmentAndMonth,
    getCurrentWeekVacations,
    getMonthSummary,

    // Real-time subscriptions
    subscribeToVacations,
    subscribeToVacationsForDay,
    unsubscribe,

    // Utility functions
    groupVacationsByDate,
    groupVacationsByStaff,
    getVacationsForDate,
    getVacationsForStaff,
    hasVacationOnDate,
    getVacationCountForDate,
    getTotalMinutesForDate,
    getTotalHoursForDate,
    getFormattedDurationForDate,
    clearVacations,
    enrichVacation: enrichVacationStable,

    // ========== VACATION ACCOUNT METHODS ==========
    vacationAccount,
    selectedYear,
    setSelectedYear,
    accountLoading,
    accountError,
    loadVacationAccount,
    createVacationAccount,
    initializeAccountWithCarryover,
    addVacationAdjustment,
    deleteVacationAdjustment,
    deleteVacationAccount,
    updateVacationAccountSettings,
    recalculateVacationAccount,
    getAllVacationAccounts,
    checkAccountExists,
    expireCarryover,
    checkSufficientDays
  };

  return (
    <VacationContext.Provider value={value}>
      {children}
    </VacationContext.Provider>
  );
};

export default VacationContext;